import { Component, signal, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { Router, RouterOutlet, ChildrenOutletContexts } from '@angular/router';
import { trigger, transition, style, animate } from '@angular/animations';
import { IonApp, IonRouterOutlet } from '@ionic/angular/standalone';
import { CookieConsentComponent, PwaInstallBannerComponent, AppNotificationModalComponent, CookieConsentService, BaseLegalContentService, BaseAppNotificationService, BaseOneSignalService, FixNbspDirective } from '@appchezandre/angular-ui';
import { IonIcon, IonText } from '@ionic/angular/standalone';
import { AccountStatus } from './models/certificate.model';
import { BottomNavComponent } from './components/bottom-nav/bottom-nav.component';
import { StorageService } from './services/storage.service';
import { AuthService } from './services/http/auth.service';
import { UserSessionService } from './services/http/user-session.service';
import { WebSocketService } from './services/websocket.service';
import { PwaUpdateService } from './services/pwa-update.service';
import { StudentEmailModalService } from './services/student-email-modal.service';
import { CertificatAttenteComponent } from './components/certificat-attente/certificat-attente.component';
import { CertificatRejeteComponent } from './components/certificat-rejete/certificat-rejete.component';
import { CertificatValideComponent } from './components/certificat-valide/certificat-valide.component';

import { addIcons } from 'ionicons';
import {
  // Toutes les icônes utilisées dans l'application (centralisées)
  add,
  addCircleOutline,
  addOutline,
  alertCircle,
  alertCircleOutline,
  arrowBackOutline,
  arrowForward,
  arrowForwardCircle,
  arrowForwardOutline,
  attachOutline,
  bagCheckOutline,
  bagHandleOutline,
  basketOutline,
  busOutline,
  calendarOutline,
  callOutline,
  cameraOutline,
  cardOutline,
  cart,
  cartOutline,
  checkmarkCircle,
  checkmarkCircleOutline,
  checkmarkOutline,
  chevronBackOutline,
  chevronDownCircleOutline,
  chevronDownOutline,
  chevronForwardOutline,
  chevronUpOutline,
  close,
  closeCircle,
  closeCircleOutline,
  closeOutline,
  cloudUploadOutline,
  createOutline,
  documentOutline,
  documentTextOutline,
  download,
  downloadOutline,
  ellipsisHorizontal,
  eyeOffOutline,
  eyeOutline,
  flashOutline,
  gift,
  giftOutline,
  grid,
  gridOutline,
  heartCircleOutline,
  heartOutline,
  home,
  homeOutline,
  hourglassOutline,
  imageOutline,
  informationCircle,
  informationCircleOutline,
  layersOutline,
  listOutline,
  locationOutline,
  lockClosedOutline,
  logIn,
  logInOutline,
  logOut,
  logOutOutline,
  mail,
  mailOutline,
  mapOutline,
  menu,
  navigateOutline,
  notificationsOutline,
  peopleOutline,
  person,
  personCircle,
  personCircleOutline,
  personOutline,
  qrCodeOutline,
  refreshOutline,
  remove,
  removeOutline,
  rocketOutline,
  school,
  schoolOutline,
  searchOutline,
  sendOutline,
  settingsOutline,
  shareSocial,
  shareSocialOutline,
  shieldCheckmark,
  shieldCheckmarkOutline,
  star,
  storefront,
  storefrontOutline,
  swapHorizontalOutline,
  thermometer,
  time,
  timeOutline,
  trashOutline,
  trendingUpOutline,
  warning,
  warningOutline,
} from 'ionicons/icons';

@Component({
  selector: 'app-root',
  imports: [IonApp, IonRouterOutlet, CookieConsentComponent, BottomNavComponent, PwaInstallBannerComponent, AppNotificationModalComponent, FixNbspDirective, CertificatAttenteComponent, CertificatRejeteComponent, CertificatValideComponent],
  templateUrl: './app.html',
  styleUrl: './app.scss',
  animations: [
    trigger('routeAnimations', [
      transition('* <=> *', [
        style({ opacity: 0 }),
        animate('200ms ease-in', style({ opacity: 1 }))
      ])
    ])
  ]
})
export class App implements OnInit, OnDestroy {
  protected readonly title = signal('LaBase_FRONTEND');
  private deferredPrompt: any;
  private userChangedSub?: Subscription;

  /** Vrai si l'utilisateur a inscrit un certificat et attend sa validation */
  readonly isAttenteUser = signal(false);

  /** Vrai si le certificat de l'utilisateur a été rejeté (statut RENOUVELLEMENT + certi_scolarite) */
  readonly isRejeteUser = signal(false);

  /** Affiche l'animation de validation après approbation du certificat */
  readonly showValideAnimation = signal(false);

  /** Motif de refus du certificat */
  readonly rejectionReason = signal<string | null>(null);

  constructor(
    private router: Router,
    private contexts: ChildrenOutletContexts,
    private oneSignalService: BaseOneSignalService,
    private storageService: StorageService,
    private authService: AuthService,
    private userSessionService: UserSessionService,
    private websocketService: WebSocketService,
    private pwaUpdateService: PwaUpdateService,
    private studentEmailModalService: StudentEmailModalService,
    public legalContentService: BaseLegalContentService,
    public cookieConsentService: CookieConsentService,
    public appNotificationService: BaseAppNotificationService
  ) {
    // Enregistrement centralisé de toutes les icônes utilisées dans l'application
    addIcons({
      add, addCircleOutline, addOutline,
      alertCircle, alertCircleOutline,
      arrowBackOutline, arrowForward, arrowForwardCircle, arrowForwardOutline,
      attachOutline,
      bagCheckOutline, bagHandleOutline, basketOutline,
      busOutline,
      calendarOutline, callOutline, cameraOutline, cardOutline,
      cart, cartOutline,
      checkmarkCircle, checkmarkCircleOutline, checkmarkOutline,
      chevronBackOutline, chevronDownCircleOutline, chevronDownOutline, chevronForwardOutline, chevronUpOutline,
      close, closeCircle, closeCircleOutline, closeOutline,
      cloudUploadOutline, createOutline,
      documentOutline, documentTextOutline,
      download, downloadOutline,
      ellipsisHorizontal,
      eyeOffOutline, eyeOutline,
      flashOutline,
      gift, giftOutline,
      grid, gridOutline,
      heartCircleOutline, heartOutline,
      home, homeOutline,
      hourglassOutline,
      imageOutline,
      informationCircle, informationCircleOutline,
      layersOutline, listOutline, locationOutline, lockClosedOutline,
      logIn, logInOutline, logOut, logOutOutline,
      mail, mailOutline, mapOutline, menu,
      navigateOutline, notificationsOutline,
      peopleOutline, person, personCircle, personCircleOutline, personOutline,
      qrCodeOutline,
      refreshOutline, remove, removeOutline, rocketOutline,
      school, schoolOutline, searchOutline, sendOutline, settingsOutline,
      shareSocial, shareSocialOutline, shieldCheckmark, shieldCheckmarkOutline,
      star, storefront, storefrontOutline, swapHorizontalOutline,
      time, timeOutline, trashOutline, trendingUpOutline,
      warning, warningOutline, thermometer
    });
  }

  ngOnInit() {
    this.setupPWAInstallPrompt();
    this.checkTokenExpiration();
    this.refreshUserProfile();
    this.initializeOneSignal();
    this.initializeUserSession();
    this.initializeWebSocket();
    // this.pwaUpdateService.startUpdateChecks(); // Démarrer la vérification de version via NGSW
    this.checkStudentEmailRequired();

    // Réagir immédiatement quand setUser() est appelé (ex: après login/register)
    // sans refaire un appel API — on lit directement depuis le storage
    this.userChangedSub = this.storageService.userChanged$.subscribe(() => {
      const user = this.storageService.getUser();
      this.isAttenteUser.set(Number(user?.statutcomptId) === AccountStatus.ATTENTE);
      this.isRejeteUser.set(Number(user?.statutcomptId) === AccountStatus.RENOUVELLEMENT && !user?.email_etudiant);
      this.rejectionReason.set(user?.rejectionReason ?? null);
    });
  }

  ngOnDestroy() {
    this.userChangedSub?.unsubscribe();
  }

  /**
   * Rafraîchit le profil depuis l'API au démarrage pour avoir le statutcomptId à jour
   */
  async refreshUserProfile() {
    if (!this.storageService.isAuthenticated()) return;
    try {
      const wasAttente = this.isAttenteUser();
      const profile = await this.authService.getProfile();
      this.storageService.setUser(profile);
      const isNowValide = Number(profile.statutcomptId) === AccountStatus.VALIDE;
      this.isAttenteUser.set(Number(profile.statutcomptId) === AccountStatus.ATTENTE);
      this.isRejeteUser.set(Number(profile.statutcomptId) === AccountStatus.RENOUVELLEMENT && !profile.email_etudiant);
      if (wasAttente && isNowValide) this.showValideAnimation.set(true);
      this.rejectionReason.set(profile.rejectionReason ?? null);

      // Régénérer le JWT avec les données fraîches (évite un token stale bloqué par le guard)
      try {
        const { access_token } = await this.authService.refreshTokenJwt();
        this.storageService.setToken(
          access_token,
          this.storageService.isRememberMeActive(),
          this.storageService.getRefreshToken() ?? undefined,
        );
      } catch { /* non-bloquant */ }
    } catch (e) {
      // Fallback sur le storage existant
      const user = this.storageService.getUser();
      this.isAttenteUser.set(Number(user?.statutcomptId) === AccountStatus.ATTENTE);
      this.isRejeteUser.set(Number(user?.statutcomptId) === AccountStatus.RENOUVELLEMENT && !user?.email_etudiant);
      this.rejectionReason.set(user?.rejectionReason ?? null);
    }
  }

  getRouteAnimationData() {
    return this.contexts.getContext('primary')?.route?.snapshot?.url?.toString() || '';
  }

  /**
   * Vérifie si le token est expiré au démarrage de l'application
   */
  private checkTokenExpiration() {
    if (this.storageService.isTokenExpired()) {
      console.log('[App] Token expiré, déconnexion automatique');
      this.storageService.clearAuth();
    } else {
      const expiry = this.storageService.getTokenExpiry();
      if (expiry) {
        console.log('[App] Token valide jusqu\'au:', expiry.toLocaleString());
      }
    }
  }

  /**
   * Initialise le suivi de session utilisateur si connecté
   */
  private initializeUserSession() {
    if (this.storageService.isAuthenticated()) {
      this.userSessionService.startHeartbeat();
    }
  }

  /**
   * Initialise la connexion WebSocket si l'utilisateur est connecté
   * Cette méthode est appelée au démarrage de l'application pour maintenir
   * la connexion WebSocket même après un refresh
   */
  private async initializeWebSocket() {
    if (this.storageService.isAuthenticated()) {
      const token = this.storageService.getToken();
      if (token) {
        try {
          await this.websocketService.connect(token);
          console.log('[App] WebSocket connecté au démarrage de l\'application');
        } catch (error) {
          console.error('[App] Erreur lors de la connexion WebSocket:', error);
        }
      }
    }
  }

  /**
   * Vérifie si l'utilisateur doit renseigner son email étudiant
   * Affiche une modal bloquante si nécessaire
   */
  private async checkStudentEmailRequired() {
    // Attendre un court délai pour que l'app soit complètement initialisée
    setTimeout(async () => {
      const modalShown = await this.studentEmailModalService.showModalIfNeeded();
      if (modalShown) {
        console.log('[App] Modal email étudiant affichée');
      }
    }, 500);
  }

  /**
   * Vérifie si l'application est en mode PWA (installée)
   */
  private isPWAMode(): boolean {
    // Méthode 1: display-mode standalone
    if (window.matchMedia('(display-mode: standalone)').matches) {
      return true;
    }

    // Méthode 2: iOS standalone mode
    if ((window.navigator as any).standalone === true) {
      return true;
    }

    // Méthode 3: Vérifier si lancé depuis l'écran d'accueil (Android)
    if (document.referrer.includes('android-app://')) {
      return true;
    }

    return false;
  }

  /**
   * Initialise OneSignal pour les notifications push
   * UNIQUEMENT en mode PWA (app installée)
   */
  private async initializeOneSignal() {
    try {
      // Vérifier si on est en mode PWA
      if (!this.isPWAMode()) {
        console.log('[OneSignal] Mode web détecté - OneSignal désactivé (PWA uniquement)');
        return;
      }

      console.log('[OneSignal] Mode PWA détecté - Début de l\'initialisation...');
      await this.oneSignalService.initialize();
      console.log('[OneSignal] Service initialisé avec succès');

      // Demander la permission après un court délai (pour ne pas être trop intrusif au démarrage)
      setTimeout(async () => {
        console.log('[OneSignal] Demande de permission...');
        const granted = await this.oneSignalService.requestPermissionIfNeeded();
        if (granted) {
          console.log('[OneSignal] ✅ Permission accordée');

          // Si connecté, configurer les tags immédiatement
          if (this.storageService.isAuthenticated()) {
            await this.configureOneSignalForUser();
          }
        } else {
          console.log('[OneSignal] ❌ Permission refusée ou déjà définie');
        }
      }, 5000); // Attend 5 secondes après l'ouverture de l'app
    } catch (error) {
      console.error('[OneSignal] Erreur lors de l\'initialisation:', error);
    }
  }

  /**
   * Configure OneSignal avec l'external_id et le tag magasin_id
   * Appelé après login ou lors de l'init si déjà connecté
   */
  private async configureOneSignalForUser() {
    try {
      const user = this.storageService.getUser();
      if (!user?.id) {
        console.log('[OneSignal] Pas d\'utilisateur connecté');
        return;
      }

      // Définir l'external_id
      await this.oneSignalService.setExternalUserId(user.id.toString());
      console.log('[OneSignal] External user ID défini:', user.id);

      // Définir le tag magasin_id
      const magasinId = user.magasinId || user.app_data?.labase?.magasinId;
      if (magasinId) {
        await this.oneSignalService.sendTags({ magasin_id: magasinId.toString() });
        console.log('[OneSignal] Tag magasin_id défini:', magasinId);
      } else {
        console.warn('[OneSignal] Utilisateur sans magasinId - tag non défini');
      }
    } catch (error) {
      console.error('[OneSignal] Erreur lors de la configuration:', error);
    }
  }

  /**
   * Déconnecte l'utilisateur et redirige vers la page de connexion
   */
  async handleLogout() {
    this.storageService.clearAuth();
    this.isAttenteUser.set(false);
    this.isRejeteUser.set(false);
    this.rejectionReason.set(null);
    this.showValideAnimation.set(false);
    await this.router.navigate(['/login']);
  }

  /**
   * Configure la gestion de l'installation PWA
   */
  private setupPWAInstallPrompt() {
    window.addEventListener('beforeinstallprompt', (e) => {
      console.log('[PWA] beforeinstallprompt event fired');
      // Empêcher le prompt par défaut
      e.preventDefault();
      // Stocker l'événement pour l'utiliser plus tard
      this.deferredPrompt = e;
      console.log('[PWA] App is installable');
    });

    // Écouter l'événement d'installation réussie
    window.addEventListener('appinstalled', () => {
      console.log('[PWA] App has been installed');
      this.deferredPrompt = null;
    });
  }

  /**
   * Méthode publique pour déclencher l'installation (peut être appelée depuis un bouton)
   */
  async promptInstall() {
    if (!this.deferredPrompt) {
      console.log('[PWA] No deferred prompt available');
      return;
    }

    // Afficher le prompt d'installation
    this.deferredPrompt.prompt();

    // Attendre la réponse de l'utilisateur
    const { outcome } = await this.deferredPrompt.userChoice;
    console.log(`[PWA] User response: ${outcome}`);

    // Nettoyer le prompt
    this.deferredPrompt = null;
  }
}
